import { useEffect, useMemo, useRef, useState } from 'react';
import { DateTime } from 'luxon';
import { Siren } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Spinner } from '@/components/ui/spinner';
import { SaveBar } from '@/components/settings/queues/save-bar';
import { Switch } from '@/components/settings/settings-controls';
import { hasPermission } from '@/lib/stores/permissions-store';
import { subscribeBotServiceHoursEvents } from '@/lib/realtime/chat-realtime-service';
import { fetchBotServiceHours, putBotServiceHours } from '@/lib/api/bot-service-hours';
import {
  botServiceHoursEqual,
  emergencyStatus,
  endEmergencyNow,
  normalizeBotServiceHours,
  removeSpecialDay,
  setNumberOverride,
  upsertSpecialDay,
  validateBotServiceHours,
} from '@/lib/bot-service-hours';
import { WeeklyHoursGrid } from './weekly-hours-grid';
import { OutOfHoursMessageEditor } from './out-of-hours-message-editor';
import { OverrideEditor, TimezoneCombobox } from './override-editor';
import { NumberOverridesSection } from './number-overrides-section';
import { SpecialDaysSection } from './special-days-section';
import { BypassPhonesEditor } from './bypass-phones-editor';
import { EmergenciesSection } from './emergencies-section';

// Editor for the org-wide bot working-hours configuration (global settings, shared): the
// defaults every channel inherits, the in-app chat and per-WhatsApp-number overrides,
// special days and emergency overrides. Loads everything in ONE fetch (the payload carries
// the gateway/number catalog); validation mirrors the server so a bad save is refused inline.
//
// Two commit paths, deliberately different:
//   - Defaults / in-app / numbers / special days / bypass numbers live in a DRAFT the save
//     bar commits as a full-replace PUT with the loaded updatedAt as the
//     optimistic-concurrency base. A 409 reseeds the baseline from a refetch but KEEPS the
//     user's draft (still dirty) so their edit survives review.
//   - Emergencies commit IMMEDIATELY: fetch fresh, rebase only the emergencies array, PUT
//     with the fresh base — never blocked by half-finished draft edits, never clobbering a
//     concurrent save. version and any unknown stored keys ride through verbatim.
//
// An older backend has no endpoint (404) → read-only "not supported" note.

function draftFromSettings(settings) {
  const clone = normalizeBotServiceHours(settings); // fresh structures for every known field
  return {
    enabled: clone.enabled,
    defaults: clone.defaults,
    appChannel: clone.appChannel,
    numbers: clone.numbers,
    specialDays: clone.specialDays,
    bypassPhones: clone.bypassPhones,
  };
}

export function BotServiceHoursEditor() {
  const [data, setData] = useState(null); // { gateways, placeholders }
  const [baseline, setBaseline] = useState(null); // { settings, updatedAt }
  const [draft, setDraft] = useState(null);
  const [serverCanEdit, setServerCanEdit] = useState(() => hasPermission('settings', 'manage'));
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [emergencyBusy, setEmergencyBusy] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [unsupported, setUnsupported] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  const seedShared = (res) => {
    setData({ gateways: res.gateways, placeholders: res.placeholders });
    setBaseline({ settings: res.settings, updatedAt: res.updatedAt });
    setServerCanEdit(Boolean(res.canEdit));
  };
  const seed = (res) => {
    seedShared(res);
    setDraft(draftFromSettings(res.settings));
  };

  useEffect(() => {
    let cancelled = false;
    fetchBotServiceHours()
      .then((res) => {
        if (cancelled) return;
        seed(res);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setIsLoading(false);
        if (error?.status === 404) {
          setUnsupported(true);
          setServerCanEdit(false);
          return;
        }
        setLoadFailed(true);
        setServerCanEdit(false);
        toast.error(error instanceof Error ? error.message : 'Could not load the bot working hours.');
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Half-minute tick so the status pills and the active-emergency banner move on their own.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const draftSettings = useMemo(() => {
    if (!baseline || !draft) return null;
    // version, emergencies and any unknown stored keys ride from the baseline.
    return {
      ...baseline.settings,
      enabled: draft.enabled,
      defaults: draft.defaults,
      appChannel: draft.appChannel,
      numbers: draft.numbers,
      specialDays: draft.specialDays,
      bypassPhones: draft.bypassPhones,
    };
  }, [baseline, draft]);

  const validation = useMemo(
    () =>
      draftSettings
        ? validateBotServiceHours(draftSettings, { placeholders: data?.placeholders || [] })
        : { ok: true, errors: {} },
    [draftSettings, data],
  );

  const isDirty = useMemo(() => {
    if (!draftSettings || !baseline) return false;
    // Both sides carry the SAME emergencies (the draft never holds them), so this reduces
    // to the draft slices.
    return !botServiceHoursEqual(draftSettings, baseline.settings);
  }, [draftSettings, baseline]);

  const editable = serverCanEdit && !unsupported && !loadFailed;

  // Live refresh: a colleague saved (the botServiceHours system.global ping) → reseed
  // silently, but ONLY while this editor is pristine. A dirty draft keeps its stale
  // baseline on purpose, so the user's own save 409s and rebases explicitly instead of
  // the ground shifting under their edits. Our own save also fires the ping; by then the
  // draft equals the baseline, so the refetch is a harmless no-op reseed.
  const refreshableRef = useRef(false);
  refreshableRef.current =
    !isDirty && !isSaving && !emergencyBusy && !isLoading && !unsupported && !loadFailed;
  useEffect(() => {
    return subscribeBotServiceHoursEvents(() => {
      if (!refreshableRef.current) return;
      fetchBotServiceHours()
        .then((res) => {
          if (!refreshableRef.current) return;
          seed(res);
        })
        .catch(() => {
          /* transient — the next ping or a manual reload retries */
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    if (!editable || !validation.ok || !isDirty || isSaving) return;
    setIsSaving(true);
    try {
      const res = await putBotServiceHours({
        settings: draftSettings,
        baseUpdatedAt: baseline.updatedAt,
      });
      seed(res);
      toast.success('Bot working hours saved for everyone.');
    } catch (error) {
      if (error?.status === 409) {
        // Someone else saved since we loaded: rebase onto their version but KEEP this draft
        // (still dirty against the fresh baseline) so nothing typed here is lost.
        try {
          const fresh = await fetchBotServiceHours();
          seedShared(fresh);
        } catch {
          /* keep the stale baseline; the user can retry */
        }
        toast.error('Settings changed elsewhere — review and save again.');
      } else {
        toast.error(error instanceof Error ? error.message : 'Could not save the bot working hours.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleDiscard = () => {
    if (!baseline) return;
    setDraft(draftFromSettings(baseline.settings));
  };

  // Immediate-commit path for emergencies: fresh GET → mutate only the emergencies array on
  // the FRESH settings → PUT with the fresh base → reseed the baseline, draft untouched.
  const commitEmergencyChange = async (mutate, successMessage) => {
    if (!editable || emergencyBusy) return false;
    setEmergencyBusy(true);
    try {
      const fresh = await fetchBotServiceHours();
      const res = await putBotServiceHours({
        settings: mutate(fresh.settings),
        baseUpdatedAt: fresh.updatedAt,
      });
      seedShared(res);
      if (successMessage) toast.success(successMessage);
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not update the emergency override.',
      );
      return false;
    } finally {
      setEmergencyBusy(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading bot working hours…
      </div>
    );
  }

  if (unsupported || loadFailed || !draft || !data) {
    return (
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto max-w-3xl">
          {unsupported ? (
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
              Bot working hours are not supported by this server yet — the bots keep their
              current behaviour until the backend is updated.
            </p>
          ) : (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              The bot working hours could not be loaded. Saving now would overwrite the stored
              values — reload before editing.
            </p>
          )}
        </div>
      </ScrollArea>
    );
  }

  const updateDefaults = (patch) =>
    setDraft((current) => ({ ...current, defaults: { ...current.defaults, ...patch } }));
  const updateAppChannel = (patch) =>
    setDraft((current) => ({ ...current, appChannel: { ...current.appChannel, ...patch } }));
  const setNumber = (botId, patch) =>
    setDraft((current) => ({
      ...current,
      numbers: setNumberOverride({ numbers: current.numbers }, botId, patch).numbers,
    }));
  const upsertDay = (day) =>
    setDraft((current) => ({
      ...current,
      specialDays: upsertSpecialDay({ specialDays: current.specialDays }, day).specialDays,
    }));
  const removeDay = (id) =>
    setDraft((current) => ({
      ...current,
      specialDays: removeSpecialDay({ specialDays: current.specialDays }, id).specialDays,
    }));
  const setBypassPhones = (bypassPhones) =>
    setDraft((current) => ({ ...current, bypassPhones }));

  const inherited = {
    enabled: draft.enabled,
    timezone: draft.defaults.timezone,
    weekly: draft.defaults.weekly,
    closeOnPublicHolidays: draft.defaults.closeOnPublicHolidays,
    mode: draft.defaults.mode,
    message: draft.defaults.message,
  };

  const nowISOUtc = new Date(nowTick).toISOString();
  const zoneNowISO = DateTime.fromMillis(nowTick).setZone(draft.defaults.timezone || 'UTC').toISO();
  const activeEmergencies = baseline.settings.emergencies.filter(
    (emergency) => emergencyStatus(emergency, nowISOUtc) === 'active',
  );

  return (
    <>
      <ScrollArea className="min-h-0 flex-1" viewportClassName="p-6">
        <div className="mx-auto flex max-w-3xl flex-col gap-8 pb-4">
          {activeEmergencies.map((emergency) => (
            <div
              key={emergency.id}
              className="flex items-center gap-3 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3"
            >
              <Siren className="size-5 shrink-0 text-destructive" aria-hidden />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-destructive">
                  Emergency override active — {emergency.label}
                </p>
                <p className="text-xs text-destructive/80">
                  {emergency.until
                    ? `Until ${DateTime.fromISO(emergency.until)
                        .setZone(draft.defaults.timezone || 'UTC')
                        .toFormat('ccc d LLL HH:mm')}`
                    : 'Until further notice'}
                  {' · '}
                  {emergency.mode === 'silent'
                    ? 'patients get no reply.'
                    : 'patients get the emergency reply.'}
                </p>
              </div>
              {editable ? (
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  disabled={emergencyBusy}
                  onClick={() =>
                    commitEmergencyChange(
                      (settings) => endEmergencyNow(settings, emergency.id, new Date().toISOString()),
                      'Emergency override ended.',
                    )
                  }
                >
                  End now
                </Button>
              ) : null}
            </div>
          ))}

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">Defaults</h3>
              <p className="text-xs text-muted-foreground">
                The base rules every channel inherits — WhatsApp numbers, the in-app chat —
                unless overridden below.
              </p>
            </div>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">Enforce working hours</p>
                <p className="text-[11px] text-muted-foreground">
                  Master switch. Off means every chatbot answers around the clock and nothing
                  below applies.
                </p>
              </div>
              <Switch
                checked={draft.enabled}
                disabled={!editable}
                label="Enforce working hours"
                onChange={(next) => setDraft((current) => ({ ...current, enabled: next }))}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">Timezone</p>
                <p className="text-[11px] text-muted-foreground">
                  The clock the weekly hours below are read in.
                </p>
              </div>
              <div>
                <TimezoneCombobox
                  value={draft.defaults.timezone}
                  disabled={!editable}
                  onChange={(zone) => updateDefaults({ timezone: zone })}
                />
                {validation.errors['defaults.timezone'] ? (
                  <p className="mt-1 text-[11px] text-destructive">
                    {validation.errors['defaults.timezone']}
                  </p>
                ) : null}
              </div>
            </div>
            <WeeklyHoursGrid
              weekly={draft.defaults.weekly}
              errors={validation.errors}
              errorPrefix="defaults.weekly"
              disabled={!editable}
              onChange={(weekly) => updateDefaults({ weekly })}
            />
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-card px-3 py-2.5">
              <div className="min-w-0">
                <p className="text-[13px] font-medium text-foreground">Public holidays</p>
                <p className="text-[11px] text-muted-foreground">
                  Treat public holidays as closed all day.
                </p>
              </div>
              <div className="flex items-center gap-2.5">
                {draft.defaults.closeOnPublicHolidays ? (
                  <div>
                    <Input
                      value={draft.defaults.holidayCountry}
                      disabled={!editable}
                      maxLength={2}
                      aria-label="Holiday country code"
                      placeholder="SG"
                      onChange={(event) =>
                        updateDefaults({ holidayCountry: event.target.value.toUpperCase() })
                      }
                      className="h-8 w-14 text-center text-sm uppercase"
                    />
                  </div>
                ) : null}
                <Switch
                  checked={draft.defaults.closeOnPublicHolidays}
                  disabled={!editable}
                  label="Close on public holidays"
                  onChange={(next) => updateDefaults({ closeOnPublicHolidays: next })}
                />
              </div>
              {validation.errors['defaults.holidayCountry'] ? (
                <p className="w-full text-[11px] text-destructive">
                  {validation.errors['defaults.holidayCountry']}
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <p className="text-[13px] font-medium text-foreground">When the clinic is closed</p>
              <OutOfHoursMessageEditor
                mode={draft.defaults.mode}
                message={draft.defaults.message}
                placeholders={data.placeholders}
                disabled={!editable}
                errors={{
                  mode: validation.errors['defaults.mode'],
                  message: validation.errors['defaults.message'],
                }}
                onChange={(patch) => updateDefaults(patch)}
              />
            </div>
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">In-app chat</h3>
              <p className="text-xs text-muted-foreground">
                The chat bot inside the patient app. Every field inherits the defaults unless
                overridden here.
              </p>
            </div>
            <OverrideEditor
              value={draft.appChannel}
              inherited={inherited}
              placeholders={data.placeholders}
              errors={validation.errors}
              errorPrefix="appChannel"
              disabled={!editable}
              onChange={updateAppChannel}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">Per-number</h3>
              <p className="text-xs text-muted-foreground">
                Override hours or replies for a specific WhatsApp number where it differs from
                the defaults.
              </p>
            </div>
            <NumberOverridesSection
              gateways={data.gateways}
              value={draft.numbers}
              inherited={inherited}
              placeholders={data.placeholders}
              errors={validation.errors}
              disabled={!editable}
              onSetNumber={setNumber}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">Special days</h3>
              <p className="text-xs text-muted-foreground">
                Date exceptions — closed all day, or different hours for one date. Saved with
                the save bar like everything above.
              </p>
            </div>
            <SpecialDaysSection
              days={draft.specialDays}
              gateways={data.gateways}
              placeholders={data.placeholders}
              errors={validation.errors}
              disabled={!editable}
              nowISO={zoneNowISO}
              onUpsert={upsertDay}
              onRemove={removeDay}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">Emergencies</h3>
              <p className="text-xs text-muted-foreground">
                Force-close on the spot — an aircon failure, a power cut. These apply the
                moment they are started, independent of the save bar.
              </p>
            </div>
            <EmergenciesSection
              emergencies={baseline.settings.emergencies}
              gateways={data.gateways}
              placeholders={data.placeholders}
              timezone={draft.defaults.timezone}
              canEdit={editable}
              busy={emergencyBusy}
              nowISO={nowISOUtc}
              onCommit={commitEmergencyChange}
            />
          </section>

          <section className="flex flex-col gap-3">
            <div>
              <h3 className="text-[15px] font-bold text-foreground">Bypass numbers</h3>
              <p className="text-xs text-muted-foreground">
                Senders the working hours never apply to — typically the team&apos;s own test
                numbers. Messages from these numbers are always answered normally: weekly
                hours, special days and emergency overrides are all ignored, and no
                out-of-service reply or hand-off happens. Matched by digit suffix, so one
                entry covers the number with or without its country code.
              </p>
            </div>
            <BypassPhonesEditor
              phones={draft.bypassPhones}
              disabled={!editable}
              error={validation.errors['bypassPhones']}
              onChange={setBypassPhones}
            />
          </section>

          {!editable ? (
            <p className="text-xs text-muted-foreground">
              You need the settings <span className="font-semibold">manage</span> permission to
              edit these.
            </p>
          ) : null}
        </div>
      </ScrollArea>
      {editable ? (
        <SaveBar
          dirty={isDirty}
          saving={isSaving}
          canSave={validation.ok}
          onSave={handleSave}
          onDiscard={handleDiscard}
          leftActions={
            isDirty && !validation.ok ? (
              <span className="text-[11.5px] font-medium text-destructive">
                Fix the highlighted fields before saving.
              </span>
            ) : null
          }
        />
      ) : null}
    </>
  );
}
